import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, PawPrint, MessagesSquare, Sprout, Footprints, MessageCircle } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal, LineReveal } from "../components/Reveal";
import { EventCard } from "../components/cards";
import { getUpcomingEvents, getPastEvents } from "../lib/content";
import { whatsappChannel, pacerClub } from "../content/site";

// C. Types of future PilCoo gatherings — NOT scheduled events, no dates
const eventTypes = [
  {
    name: "Community Action",
    icon: PawPrint,
    pillar: "Animals",
    color: "#43D2FF",
    text: "#0B7A8C",
    on: "#1C1917",
    desc: "Walks, feeding initiatives, animal-welfare drives and opportunities to show up together.",
  },
  {
    name: "Conversations & Wellbeing",
    icon: MessagesSquare,
    pillar: "People",
    color: "#FF2F89",
    text: "#D6106A",
    on: "#F7F5F0",
    desc: "Community conversations, awareness sessions and safe spaces centred around mental wellbeing.",
  },
  {
    name: "Nature in Action",
    icon: Sprout,
    pillar: "Nature",
    color: "#FFC300",
    text: "#8F6D00",
    on: "#1C1917",
    desc: "Clean-ups, planting initiatives and community-led environmental action.",
  },
];

// D. Ways to participate today
const joinOptions = [
  {
    key: "pacer",
    icon: Footprints,
    color: "#43D2FF",
    text: "#0B7A8C",
    title: "Join our Pacer community",
    desc: "For walking challenges and movement-led initiatives.",
    cta: "Join our Pacer Community",
    url: pacerClub.url,
    testid: "events-pacer-cta",
    label: "Join the PilCoo Pacer community (opens in new tab)",
  },
  {
    key: "whatsapp",
    icon: MessageCircle,
    color: "#FF2F89",
    text: "#D6106A",
    title: "Follow our WhatsApp Channel",
    desc: "For event announcements, activities, volunteering opportunities and PilCoo updates.",
    cta: "Follow the channel",
    url: whatsappChannel.url,
    testid: "events-whatsapp-channel-cta",
    label: "Follow the PilCoo WhatsApp Channel (opens in new tab)",
  },
];

export default function Events() {
  const upcoming = getUpcomingEvents();
  const past = getPastEvents();

  return (
    <main>
      <SEO
        title="Events"
        description="Walks, conversations, drives and gatherings happening at a specific time and place — online or offline."
        path="/events"
      />

      {/* A. HERO */}
      <section className="pt-32 md:pt-44 pb-14 md:pb-16" data-testid="events-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">Events</p>
          <h1 className="mt-6 font-serif tracking-tighter leading-[0.98] text-ink text-5xl sm:text-6xl lg:text-7xl">
            <LineReveal lines={["Show up. Together."]} delay={0.15} />
          </h1>
          <Reveal delay={0.4}>
            <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-ink-2">
              Walks, conversations, drives and gatherings where the PilCoo
              community comes together — online or offline. (For what we're doing
              together over time, see our{" "}
              <Link to="/activities" className="underline underline-offset-4 text-ink font-medium">
                Activities
              </Link>
              .)
            </p>
          </Reveal>
        </div>
      </section>

      {upcoming.length > 0 && (
        <section className="pb-20 md:pb-24" data-testid="events-upcoming">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-ink">Upcoming Events</h2>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcoming.map((event, i) => (
                <Reveal key={event.slug} delay={i * 0.08}>
                  <EventCard event={event} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* B. INTENTIONAL EMPTY STATE */}
      {upcoming.length === 0 && (
        <section className="border-y border-line bg-sand-2 py-20 md:py-28" data-testid="events-empty">
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">
                Nothing on the calendar — yet
              </p>
              <h2 className="mt-5 font-serif text-4xl md:text-6xl tracking-tight leading-[1.05] text-ink">
                Something kind is <em className="text-ink-2">taking shape.</em>
              </h2>
              <p className="mt-7 max-w-xl mx-auto text-base md:text-lg leading-relaxed text-ink-2">
                We don't have a gathering to announce just yet. When the next
                PilCoo walk, conversation, community drive or meetup is ready,
                you'll find it here.
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="mt-12 flex flex-col sm:flex-row items-start justify-center gap-10 sm:gap-14">
                <div className="flex flex-col items-center gap-3">
                  <a
                    href={whatsappChannel.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Follow PilCoo on WhatsApp for updates (opens in new tab)"
                    data-testid="events-whatsapp-cta"
                    className="rounded-full bg-action text-sand px-8 py-4 text-sm font-semibold inline-flex items-center gap-2 transition-transform duration-300 hover:scale-[1.04] active:scale-95"
                  >
                    Follow on WhatsApp
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                  <p className="max-w-[15rem] text-xs leading-relaxed text-ink-2">
                    Be the first to hear about new events and opportunities.
                  </p>
                </div>
                <div className="flex flex-col items-center gap-3">
                  <Link
                    to="/activities"
                    data-testid="events-activities-link"
                    className="group rounded-full border border-ink px-8 py-4 text-sm font-semibold text-ink inline-flex items-center gap-2 transition-colors duration-300 hover:bg-ink hover:text-sand"
                  >
                    Explore Activities
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                  <p className="max-w-[15rem] text-xs leading-relaxed text-ink-2">
                    You don't need to wait for an event to participate.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* C. WHAT YOU CAN EXPECT — types of future gatherings */}
      <section className="py-20 md:py-28" data-testid="events-expect">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">What you can expect</p>
            <h2 className="mt-4 font-serif text-3xl md:text-5xl tracking-tight text-ink max-w-3xl">
              The kinds of gatherings <em className="text-ink-2">PilCoo will host.</em>
            </h2>
            <p className="mt-5 max-w-2xl text-sm md:text-base leading-relaxed text-ink-2">
              These describe the types of events PilCoo is building toward —
              none of them are scheduled yet. When one is confirmed, it appears
              above with a date, a place and a way to join.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {eventTypes.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.08}>
                <div
                  className="h-full rounded-xl border p-7 md:p-8"
                  style={{ backgroundColor: `${t.color}14`, borderColor: `${t.color}4D` }}
                  data-testid={`event-type-${t.pillar.toLowerCase()}`}
                >
                  <span
                    className="flex h-12 w-12 items-center justify-center rounded-full"
                    style={{ backgroundColor: t.color }}
                  >
                    <t.icon className="h-6 w-6" style={{ color: t.on }} aria-hidden="true" />
                  </span>
                  <p
                    className="mt-5 text-[0.65rem] font-semibold uppercase tracking-[0.2em]"
                    style={{ color: t.text }}
                  >
                    {t.pillar}
                  </p>
                  <h3 className="mt-2 font-serif text-2xl tracking-tight text-ink">{t.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-2">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* D. DON'T WAIT FOR THE NEXT EVENT */}
      <section className="pb-24 md:pb-32" data-testid="events-join">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight text-ink max-w-2xl">
              Don't wait for the next event <em className="text-ink-2">to join us.</em>
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {joinOptions.map((opt, i) => (
              <Reveal key={opt.key} delay={i * 0.08}>
                <a
                  href={opt.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={opt.label}
                  data-testid={opt.testid}
                  className="group block h-full rounded-xl border border-line bg-sand p-8 md:p-10 transition-colors duration-300 hover:border-ink/40"
                >
                  <span
                    className="flex h-12 w-12 items-center justify-center rounded-full border"
                    style={{ borderColor: `${opt.color}80`, backgroundColor: `${opt.color}1A` }}
                  >
                    <opt.icon className="h-6 w-6" style={{ color: opt.text }} aria-hidden="true" />
                  </span>
                  <h3 className="mt-6 font-serif text-2xl md:text-3xl tracking-tight text-ink">{opt.title}</h3>
                  <p className="mt-3 text-sm md:text-base leading-relaxed text-ink-2 max-w-md">{opt.desc}</p>
                  <span
                    className="mt-7 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-transform duration-300 group-hover:scale-[1.03]"
                    style={{ backgroundColor: opt.color, color: opt.color === "#FF2F89" ? "#F7F5F0" : "#1C1917" }}
                  >
                    {opt.cta}
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {past.length > 0 && (
        <section className="pb-24 md:pb-32" data-testid="events-past">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-ink">Past Events</h2>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-ink-2">
                Past event pages become recaps — with documented outcomes and
                photos published as they're verified, never invented.
              </p>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {past.map((event, i) => (
                <Reveal key={event.slug} delay={i * 0.08}>
                  <EventCard event={event} past />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
